const IMG_HERO = `${process.env.PUBLIC_URL || ""}/images/far-couple-shot.jpg`;

const IMG_OVERLAP = `${process.env.PUBLIC_URL || ""}/images/close-couple-shot.jpg`;

const pub = process.env.PUBLIC_URL || "";
const IMG_SK_SOLO = `${pub}/images/far-solo-storm-king-shot.jpeg`;
const IMG_SK_FENCE = `${pub}/images/reflective-fence-storm-king.jpeg`;
const IMG_SK_PICNIC = `${pub}/images/birthday-picnic-shot.jpeg`;
const IMG_SK_LILA = `${pub}/images/with-lila-shot.jpeg`;
const IMG_SK_COUPLE = `${pub}/images/couple-shot-storm-king.jpeg`;

export default function AboutPage() {
  return (
    <main className="pb-24 pt-28 font-body md:pt-32">
      {/* Section 1: Hero Editorial */}
      <section className="relative mb-24 mt-8 px-5 sm:px-6 md:mb-40 md:mt-12 md:px-12">
        <div className="flex flex-col items-start gap-8 sm:gap-10 md:flex-row md:gap-12">
          <div className="w-full md:w-1/2">
            <h1 className="font-headline text-[clamp(3.5rem,18vw,7.5rem)] leading-[0.95] tracking-[-0.05em] text-primary opacity-90 md:text-[12rem] md:leading-none">
              ABOUT
            </h1>
          </div>
          <div className="relative aspect-[4/5] min-h-[240px] w-full overflow-hidden sm:aspect-[3/4] sm:min-h-[280px] md:aspect-auto md:h-[600px] md:min-h-0 md:w-1/2">
            <img
              src={IMG_HERO}
              alt="Sofia and Jason on an ornate stone staircase outdoors, embracing with snow on the steps"
              className="h-full w-full object-cover object-center"
            />
          </div>
        </div>
      </section>

      {/* Section 2: Our Dialogue (Asymmetric Color Blocks) */}
      <section className="relative mb-28 md:mb-60">
        <div className="relative grid grid-cols-12 gap-0">
          <div className="relative z-10 col-span-12 flex flex-col justify-center bg-tertiary-fixed-dim px-6 py-10 sm:p-10 md:col-span-7 md:p-24">
            <h2 className="mb-8 font-headline text-4xl text-on-tertiary-fixed sm:mb-10 sm:text-5xl md:mb-12">
              How We Got Here
            </h2>
            <div className="max-w-xl space-y-6 font-body text-base leading-relaxed text-on-tertiary-container sm:space-y-8 sm:text-lg">
              <p>
                While Sofia thought she was interviewing for a job, Jason had
                other plans. It took Sofia&apos;s move to Austin, Texas to make
                it real: a new city, a new job, and a rekindled something that
                turned into a cross-country love story. Sofia eventually came
                back to New York, where they&apos;ve built their life together.
                Six years of good food, far-flung places, and a shared belief
                that more is always more.
              </p>
            </div>
          </div>
          {/* In flow on small screens; overlapping panel on md+ */}
          <div className="relative z-20 col-span-12 mx-auto mt-2 w-full max-w-md px-6 pb-2 md:absolute md:right-[15%] md:top-[-10%] md:mx-0 md:mt-0 md:w-1/3 md:max-w-none md:px-0 md:pb-0">
            <img
              src={IMG_OVERLAP}
              alt="Sofia and Jason embracing in front of Bethesda Fountain in Central Park"
              className="aspect-[3/4] h-auto w-full object-cover object-center shadow-[0_20px_50px_rgba(52,48,34,0.18)] md:shadow-none"
            />
          </div>
          <div className="relative z-0 hidden col-span-12 bg-secondary-fixed-dim p-12 md:col-span-5 md:mt-40 md:block md:p-20">
            <div className="md:pt-40">
              <h3 className="mb-6 font-headline text-3xl uppercase tracking-tighter text-on-secondary-fixed">
                Our Dialogue
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: The Monumental Stage (Storm King) */}
      <section className="mb-24 px-5 sm:px-6 md:mb-40 md:px-12">
        <div className="relative overflow-visible bg-surface-container-highest p-5 sm:p-8 md:p-12">
          <div className="flex flex-col items-center gap-12 sm:gap-16 lg:flex-row lg:gap-20">
            {/* Mobile / tablet: 2×2 grid + featured couple — avoids pinching the 12×12 collage */}
            <div className="order-2 flex w-full max-w-2xl flex-col gap-4 lg:hidden">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={IMG_SK_SOLO}
                    alt="Sofia walking on the grass at Storm King with a red sculpture on the hill"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={IMG_SK_FENCE}
                    alt="Sofia and Jason reflected in the mirrored picket fence at Storm King"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={IMG_SK_PICNIC}
                    alt="Birthday picnic on the lawn at Storm King"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={IMG_SK_LILA}
                    alt="Sofia, Jason, and their baby in a grassy field at Storm King"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </div>
              <div className="flex justify-center pt-1">
                <div className="aspect-[3/4] w-[min(100%,18rem)] overflow-hidden shadow-[0_28px_56px_rgba(52,48,34,0.22)] sm:w-[min(100%,22rem)]">
                  <img
                    src={IMG_SK_COUPLE}
                    alt="Sofia and Jason close together at Storm King"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </div>
            </div>

            {/* Desktop: layered collage */}
            <div className="relative order-2 hidden h-[800px] w-full grid-cols-12 grid-rows-12 gap-2 lg:order-1 lg:grid lg:w-3/5">
              {/* 1 — top left: solo figure in field, red sculpture */}
              <div className="relative z-0 col-span-6 col-start-1 row-span-7 row-start-1 overflow-hidden">
                <img
                  src={IMG_SK_SOLO}
                  alt="Sofia walking on the grass at Storm King with a red sculpture on the hill"
                  className="h-full w-full object-cover object-center"
                />
              </div>
              {/* 2 — top right: mirrored fence */}
              <div className="relative z-0 col-span-6 col-start-7 row-span-4 row-start-2 overflow-hidden">
                <img
                  src={IMG_SK_FENCE}
                  alt="Sofia and Jason reflected in the mirrored picket fence at Storm King"
                  className="h-full w-full object-cover object-center"
                />
              </div>
              {/* 3 — bottom left: birthday picnic */}
              <div className="relative z-0 col-span-6 col-start-1 row-span-5 row-start-8 overflow-hidden">
                <img
                  src={IMG_SK_PICNIC}
                  alt="Birthday picnic on the lawn at Storm King"
                  className="h-full w-full object-cover object-center"
                />
              </div>
              {/* 4 — bottom right: family with baby */}
              <div className="relative z-0 col-span-6 col-start-7 row-span-6 row-start-7 overflow-hidden">
                <img
                  src={IMG_SK_LILA}
                  alt="Sofia, Jason, and their baby in a grassy field at Storm King"
                  className="h-full w-full object-cover object-center"
                />
              </div>
              {/* 5 — center, in front of all four (70% scale to reduce overlap) */}
              <div className="relative z-20 col-span-6 col-start-4 row-span-5 row-start-5 flex items-center justify-center">
                <div className="h-[70%] w-[70%] overflow-hidden shadow-[0_28px_56px_rgba(52,48,34,0.22)]">
                  <img
                    src={IMG_SK_COUPLE}
                    alt="Sofia and Jason close together at Storm King"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
            <div className="relative z-50 order-1 w-full px-1 sm:px-2 lg:order-2 lg:w-2/5 lg:px-0">
              <span className="mb-4 block font-label text-sm uppercase tracking-[0.3em] text-primary sm:mb-6">
                The Venue
              </span>
              <h2 className="mb-6 font-headline text-4xl leading-tight text-on-surface sm:mb-8 sm:text-5xl md:text-6xl lg:text-7xl">
                Storm King & Us
              </h2>
              <div className="space-y-4 font-body text-base leading-relaxed text-on-surface-variant sm:text-lg">
                <p>
                  Storm King Art Center is a 500-acre open-air museum in the
                  Hudson Valley, about an hour north of New York City — home to
                  over 100 monumental sculptures by some of the most celebrated
                  artists of the last century, set across rolling hills and open
                  sky.
                </p>
                <p>
                  From the very beginning, it&apos;s been part of Sofia and
                  Jason&apos;s story. Early trips up the Hudson Valley,
                  birthdays, picnics with family and friends — Storm King has
                  been a constant, a place returned to again and again.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
